<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useThreads } from '../composables/useThreads';
import ConversationsList from './ConversationsList.vue';
import ChatWindow from './ChatWindow.vue';
import StatusPanel from './StatusPanel.vue';
import { type Thread, type Message, threadsAPI } from '../services/api';
import { useAuth } from '../composables/useAuth';

const router = useRouter();
const route = useRoute();
const { user, fetchUser } = useAuth();
const {
  conversations,
  isLoading,
  hasMore,
  isArchive,
  loadThreads,
  handleLoadMore,
  toggleArchive: _toggleArchive,
  setupOrganizationChannel,
  teardownOrganizationChannel,
} = useThreads();

const toggleArchive = () => {
  _toggleArchive();
  router.replace({ query: { ...route.query, archive: isArchive.value ? 'true' : undefined } });
};

// Export types for other components
export type { Message, Thread };

const selectedConversation = ref<Thread | null>(null);
const isAiTyping = ref(false);

const handleSelectConversation = (conversation: Thread) => {
  selectedConversation.value = conversation;
  // Update URL to reflect selected conversation
  router.push(`/messenger/${conversation.id}`);
};

const handleMessengerMessage = (event: MessageEvent) => {
  if (event.data?.type === 'messenger-connected') {
    // Handle messenger connection event if needed
  }
};

const handleUpdateConversation = (updatedConversation: Thread) => {
  // Update selected conversation
  selectedConversation.value = updatedConversation;

  // Update conversation in the list
  const index = conversations.value.findIndex((c) => c.id === updatedConversation.id);
  if (index !== -1) {
    conversations.value[index] = updatedConversation;
  }
};

// Watch for URL changes (browser back/forward)
watch(
  () => route.params.id,
  async (newId) => {
    if (newId) {
      selectedConversation.value = await threadsAPI.getThread(newId as string);
    } else {
      // No ID in URL - show welcome screen
      selectedConversation.value = null;
    }
  },
);

onMounted(async () => {
  // Ensure user data is loaded before loading threads
  if (!user.value) {
    const userData = await fetchUser();
    if (!userData) {
      router.push('/login');
      return;
    }
  }

  window.addEventListener('message', handleMessengerMessage);

  if (route.query.archive === 'true') {
    isArchive.value = true;
  }

  await loadThreads(1);

  // Select conversation based on URL parameter
  const threadId = route.params.id as string;
  if (threadId) {
    selectedConversation.value = await threadsAPI.getThread(threadId);
  }

  if (user.value?.organization_id) {
    setupOrganizationChannel(user.value.organization_id);
  }
});

onUnmounted(() => {
  teardownOrganizationChannel();
  window.removeEventListener('message', handleMessengerMessage);
});
</script>

<template>
  <div class="flex flex-col overflow-hidden bg-[#0A0A0B]">
    <!-- Main Content -->
    <div class="flex flex-1 overflow-hidden relative">
      <!-- Left Sidebar - Conversations List; on mobile it is the whole screen
           until a conversation is picked, since there is no separate list page -->
      <div
        :class="[
          'flex-col lg:flex lg:w-80 lg:flex-shrink-0',
          selectedConversation ? 'hidden' : 'flex w-full',
        ]"
      >
        <ConversationsList
          :conversations="conversations"
          :selected-id="selectedConversation?.id || ''"
          :is-loading="isLoading"
          :has-more="hasMore"
          :is-archive="isArchive"
          @select-conversation="handleSelectConversation"
          @load-more="handleLoadMore"
          @toggle-archive="toggleArchive"
        />
      </div>

      <!-- Main Chat Area -->
      <div
        :class="[
          'flex-1 flex-col lg:flex lg:flex-row overflow-hidden',
          selectedConversation ? 'flex' : 'hidden',
        ]"
      >
        <ChatWindow
          v-if="selectedConversation"
          :conversation="selectedConversation"
          :is-ai-typing="isAiTyping"
          @update-conversation="handleUpdateConversation"
          class="flex-1"
        />
        <div v-else class="flex-1 flex items-center justify-center bg-[#0A0A0B]">
          <div class="text-center px-4">
            <div
              class="w-16 h-16 bg-[#111113] rounded-3xl flex items-center justify-center mx-auto mb-4"
            >
              <svg
                class="w-8 h-8 text-[#9E9080]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <h2 class="text-xl text-[#F5F0EA] mb-2">
              {{ $t('messenger.welcome_title') }}
            </h2>
            <p class="text-[#524840] mb-4">
              {{ $t('messenger.welcome_subtitle') }}
            </p>
          </div>
        </div>

        <!-- Right Sidebar - Status Panel (Hidden on mobile) -->
        <StatusPanel
          v-if="selectedConversation"
          :conversation="selectedConversation"
          @update-conversation="handleUpdateConversation"
          class="hidden xl:flex"
        />
      </div>
    </div>
  </div>
</template>
